import { NavLink } from 'react-router-dom';
import { useState } from 'react';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { useAuthContext } from '../../features/auth/context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { getCurrentEnvironment } from '../../config/environments';

/**
 * Sidebar Navigation Component
 * 📑 Provides main navigation menu
 */
const Sidebar = () => {
  const { user } = useAuthContext();
  const { themeColors } = useTheme();
  const [expandedMenus, setExpandedMenus] = useState({
    transactions: true
  });

  // Check if user has a partner
  const hasPartner = !!user?.partner || !!user?.createdPartner;
  
  // Check if user is admin
  const isAdmin = user?.isAdmin === true;
  
  // Check environment - Journeys should be visible in staging, hidden in production
  const environment = getCurrentEnvironment();
  const isStaging = !environment.isProduction && !environment.isDevelopment;
  const showJourneys = isStaging || isAdmin; // Show in staging for all users, or if admin in any environment

  const toggleMenu = (menuId) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  const navItems = [
    { path: '/dashboard', label: '📊 Dashboard', isMainNav: true },
    { path: '/wallet', label: '💰 Wallet', isMainNav: true },
    { path: '/cashflow', label: '💵 Cash Flow', isMainNav: true },
    { path: '/cashflow/income', label: '💰 Income', isSubMenu: true },
    { path: '/cashflow/expenses', label: '💳 Expenses', isSubMenu: true },
    { path: '/cashflow/budget', label: '📅 Budget', isSubMenu: true },
    { path: '/cashflow/debt', label: '🏦 Debt', isSubMenu: true },
    { path: '/cashflow/savings', label: '🎯 Savings', isSubMenu: true },
    { path: '/cashflow/assets', label: '🏠 Assets', isSubMenu: true },
    // { path: '/budgeting', label: '📅 Budget Planning' }, // Hidden for now
    // Journeys - Visible in staging for all users, or if admin in any environment
    ...(showJourneys ? [{ path: '/journeys', label: '🗺️ Journeys', isMainNav: true }] : []),
    { path: '/transactions', label: '📝 Transactions', isMainNav: true },
    
    /* Original dropdown menu - commented out as requested
    { 
      id: 'transactions',
      label: '📝 Transactions', 
      children: [
        { path: '/transactions', label: 'All Transactions' },
        { path: '/transactions/recurring', label: 'Recurring' },
        { path: '/transactions/categories', label: 'Categories' },
        { path: '/transactions/import', label: 'Import' },
      ]
    },
    */
  ];

  return (
    <div className="w-52 bg-white shadow-lg border-r border-[var(--border-primary)]/50 relative" style={{ backgroundColor: 'var(--bg-primary, #FFFFFF)' }}>
      {/* Theme-aware accent gradient */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{
          background: `linear-gradient(to bottom right, ${themeColors.primary50}, ${themeColors.primary100}, transparent)`,
        }}
      />
      <nav className="mt-8 px-3 relative z-10">
        <div className="space-y-1.5">
          {navItems.map((item) => (
            item.children ? (
              <div key={item.id} className="pb-1">
                <button
                  onClick={() => toggleMenu(item.id)}
                  className="w-full group flex items-center justify-between px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 text-[var(--text-secondary)] hover:bg-[var(--bg-primary)] hover:shadow-sm"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = themeColors.primary;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = 'var(--text-secondary)';
                  }}
                >
                  <span>{item.label}</span>
                  {expandedMenus[item.id] ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />}
                </button>
                
                {expandedMenus[item.id] && (
                  <div className="ml-4 mt-1 space-y-1 border-l-2 border-[var(--border-primary)] pl-2">
                    {item.children.map((child) => (
                      <NavLink
                        key={child.path}
                        to={child.path}
                        className={({ isActive }) =>
                          `group flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                            isActive
                              ? 'bg-[var(--bg-primary)] shadow-md transform scale-[0.98]'
                              : 'text-[var(--text-secondary)] hover:bg-[var(--bg-primary)] hover:shadow-sm hover:translate-x-0.5'
                          }`
                        }
                        style={({ isActive }) => {
                          if (isActive) {
                            return { 
                              color: themeColors.primary,
                              backgroundColor: themeColors.primary50, // Light background for better contrast
                            };
                          }
                          return {};
                        }}
                        onMouseEnter={(e) => {
                          const isActive = window.location.pathname === child.path;
                          if (!isActive) {
                            e.currentTarget.style.color = themeColors.primary;
                            e.currentTarget.style.backgroundColor = themeColors.primary50;
                          }
                        }}
                        onMouseLeave={(e) => {
                          const isActive = window.location.pathname === child.path;
                          if (!isActive) {
                            e.currentTarget.style.color = 'var(--text-secondary)';
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        {child.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => {
                  const baseClasses = `group flex items-center py-3 text-sm rounded-lg transition-all duration-300 transform-gpu`;
                  const mainNavClasses = item.isMainNav 
                    ? `px-4 font-bold` 
                    : `px-8 ml-4 font-medium`;
                  
                  const activeClasses = isActive
                    ? 'shadow-lg text-white scale-105'
                    : `text-gray-600 hover:shadow-lg hover:scale-105 ${
                        item.isMainNav 
                          ? 'hover:translate-x-2 hover:-rotate-1' 
                          : 'hover:translate-x-1'
                      }`;
                  
                  return `${baseClasses} ${mainNavClasses} ${activeClasses}`;
                }}
                style={({ isActive }) => {
                  if (isActive) {
                    return {
                      background: `linear-gradient(to right, ${themeColors.primary}, ${themeColors.secondary})`,
                      boxShadow: `0 10px 15px -3px ${themeColors.primary}40, 0 4px 6px -2px ${themeColors.primary}20`,
                      color: '#FFFFFF', // Ensure white text for contrast
                    };
                  }
                  return {};
                }}
                onMouseEnter={(e) => {
                  const isActive = window.location.pathname === item.path;
                  if (!isActive) {
                    e.currentTarget.style.background = `linear-gradient(to right, ${themeColors.primary50}, ${themeColors.primary100})`;
                    e.currentTarget.style.color = themeColors.primary;
                    e.currentTarget.style.boxShadow = `0 10px 15px -3px ${themeColors.primary}20, 0 4px 6px -2px ${themeColors.primary}10`;
                  }
                }}
                onMouseLeave={(e) => {
                  const isActive = window.location.pathname === item.path;
                  if (!isActive) {
                    e.currentTarget.style.background = '';
                    e.currentTarget.style.color = 'var(--text-secondary)';
                    e.currentTarget.style.boxShadow = '';
                  }
                }}
              >
                <span className="transition-transform duration-300 group-hover:scale-125 group-hover:rotate-12">
                  {item.label.split(' ')[0]}
                </span>
                <span className={`ml-2 transition-all duration-300 ${item.isMainNav ? 'group-hover:font-bold' : 'group-hover:font-semibold'}`}>
                  {item.label.split(' ').slice(1).join(' ')}
                </span>
              </NavLink>
            )
          ))}
        </div>
      </nav>
    </div>
  );
};

export default Sidebar; 